import React, { useState } from "react";
import * as engineService from "@/shared/api/services/engineService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Badge } from "@/shared/components/ui/badge";
import { Switch } from "@/shared/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Power,
  PowerOff,
  Building2,
  CheckCircle2,
  XCircle,
  Clock,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
} from "lucide-react";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getProviderName } from "@/shared/utils";

const statusConfig = {
  ACTIVE: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "Active" },
  active: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "Active" },
  SUSPENDED: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Suspended" },
  suspended: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Suspended" },
  INACTIVE: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Inactive" },
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Pending" },
};

const providers = [
  { code: "SCB", name: "ธนาคารไทยพาณิชย์", nameEn: "Siam Commercial Bank" },
  { code: "KBANK", name: "ธนาคารกสิกรไทย", nameEn: "Kasikorn Bank" },
  { code: "BBL", name: "ธนาคารกรุงเทพ", nameEn: "Bangkok Bank" },
  { code: "KTB", name: "ธนาคารกรุงไทย", nameEn: "Krungthai Bank" },
  { code: "TRUEWALLET", name: "ทรูมันนี่ วอลเล็ท", nameEn: "TrueMoney Wallet" },
  { code: "PROMPTPAY", name: "พร้อมเพย์ QR", nameEn: "PromptPay QR" },
];

function generateApiKey() {
  return "pk_" + Array.from({ length: 32 }, () =>
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)]
  ).join("");
}

export default function EngineMerchants() {
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [suspendDialog, setSuspendDialog] = useState(null);
  const queryClient = useQueryClient();
  const { t, language } = useLanguage();

  const resellerName = "Gaming Partners Ltd";

  const { data: merchants = [], isLoading } = useQuery({
    queryKey: ["engine-merchants"],
    queryFn: () => engineService.getMyMerchants(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => engineService.createMerchant(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["engine-merchants"]);
      setFormOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => engineService.updateMerchant(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["engine-merchants"]);
      setFormOpen(false);
      setSelectedMerchant(null);
      setSuspendDialog(null);
    },
  });

  const filteredMerchants = merchants.filter(m =>
    m.name?.toLowerCase().includes(search.toLowerCase()) ||
    m.code?.toLowerCase().includes(search.toLowerCase())
  );

  const getTodayVolume = (merchantCode) => {
    // TODO: Implement transaction volume query from backend
    // For now, return placeholder value
    return 0;
  };

  const handleSave = async (data) => {
    if (selectedMerchant) {
      // Update merchant
      await updateMutation.mutateAsync({ id: selectedMerchant.id, data });
    } else {
      // Create merchant - only send backend-required fields
      const createData = {
        email: data.email,
        password: data.password,
        name: data.name,
        webhook: data.webhook_url || undefined,
        callback: data.callback_url || undefined,
      };
      await createMutation.mutateAsync(createData);
    }
  };

  const handleToggleStatus = async () => {
    if (!suspendDialog) return;
    const currentStatus = suspendDialog.status?.toUpperCase();
    const newStatus = currentStatus === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    await updateMutation.mutateAsync({
      id: suspendDialog.id,
      data: { name: suspendDialog.name, status: newStatus },
    });
  };

  return (
    <EngineLayout currentPage="EngineMerchants" resellerName={resellerName}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("merchants")}</h1>
            <p className="text-slate-500">{merchants.length} {t("merchantsRegisteredCount")}</p>
          </div>
          <Button onClick={() => setFormOpen(true)} className="gap-2 bg-sky-600 hover:bg-sky-700">
            <Plus className="h-4 w-4" />
            {t("addMerchant")}
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder={t("searchMerchantsPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">{t("merchant")}</TableHead>
                <TableHead className="font-semibold">{t("code")}</TableHead>
                <TableHead className="font-semibold">{t("status")}</TableHead>
                <TableHead className="font-semibold">{t("todaysVolume")}</TableHead>
                <TableHead className="font-semibold">{t("created")}</TableHead>
                <TableHead className="font-semibold w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                    {t("loading")}
                  </TableCell>
                </TableRow>
              ) : filteredMerchants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                    {t("noMerchantsFound")}
                  </TableCell>
                </TableRow>
              ) : (
                filteredMerchants.map((merchant, index) => {
                  const status = statusConfig[merchant.status] || statusConfig.pending;
                  const StatusIcon = status.icon;
                  const todayVolume = getTodayVolume(merchant.code);

                  return (
                    <motion.tr
                      key={merchant.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.02 * index }}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-sky-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{merchant.name}</p>
                            <p className="text-sm text-slate-500">{merchant.contact_email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">{merchant.code}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${status.color} gap-1 border-0`}>
                          <StatusIcon className="h-3 w-3" />
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold text-slate-900">
                          ฿{todayVolume.toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {merchant.created_date ? format(new Date(merchant.created_date), "MMM d, yyyy") : "N/A"}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => {
                              setSelectedMerchant(merchant);
                              setFormOpen(true);
                            }}>
                              <Pencil className="h-4 w-4 mr-2" />
                              {t("edit")}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setSuspendDialog(merchant)}>
                              {merchant.status === "active" ? (
                                <>
                                  <PowerOff className="h-4 w-4 mr-2" />
                                  {t("suspend")}
                                </>
                              ) : (
                                <>
                                  <Power className="h-4 w-4 mr-2" />
                                  {t("activate")}
                                </>
                              )}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </motion.tr>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Merchant Form Modal */}
        <MerchantFormModal
          merchant={selectedMerchant}
          open={formOpen}
          onClose={() => {
            setFormOpen(false);
            setSelectedMerchant(null);
          }}
          onSave={handleSave}
          providers={providers}
        />

        {/* Suspend Confirmation */}
        <AlertDialog open={!!suspendDialog} onOpenChange={() => setSuspendDialog(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {suspendDialog?.status === "active" ? t("suspendMerchantTitle") : t("activateMerchantTitle")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {suspendDialog?.status === "active"
                  ? `${t("suspendMerchantConfirm")} "${suspendDialog?.name}"? ${t("willNotProcessTransactions")}`
                  : `${t("activateMerchantConfirm")} "${suspendDialog?.name}"?`
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleToggleStatus}
                className={suspendDialog?.status === "active" ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}
              >
                {suspendDialog?.status === "active" ? t("suspend") : t("activate")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </EngineLayout>
  );
}

function MerchantFormModal({ merchant, open, onClose, onSave, providers }) {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    email: "",
    password: "",
    contact_email: "",
    callback_url: "",
    webhook_url: "",
    deposit_enabled: true,
    withdrawal_enabled: true,
    fee_value: 2.5,
    min_deposit: 100,
    max_deposit: 100000,
  });
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [showApiKey, setShowApiKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (merchant) {
      setFormData(merchant);
      setSelectedProviders(merchant.notes?.split(",").filter(Boolean) || []);
    } else {
      setFormData({
        name: "",
        code: "",
        email: "",
        password: "",
        contact_email: "",
        callback_url: "",
        webhook_url: "",
        deposit_enabled: true,
        withdrawal_enabled: true,
        fee_value: 2.5,
        min_deposit: 100,
        max_deposit: 100000,
      });
      setSelectedProviders([]);
    }
  }, [merchant, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleProvider = (code) => {
    setSelectedProviders(prev =>
      prev.includes(code) ? prev.filter(p => p !== code) : [...prev, code]
    );
  };

  const handleSubmit = async () => {
    setSaving(true);
    await onSave({
      ...formData,
      notes: selectedProviders.join(","),
    });
    setSaving(false);
  };

  const copyApiKey = () => {
    if (merchant?.api_key) {
      navigator.clipboard.writeText(merchant.api_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{merchant ? t("editMerchant") : t("createNewMerchant")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("businessNameLabel")} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder={t("merchantNamePlaceholder")}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("merchantCode")} *</Label>
              <Input
                value={formData.code}
                onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
                placeholder={t("merchantCodePlaceholder")}
                disabled={!!merchant}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("emailLabel")} *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              placeholder={t("emailPlaceholder")}
              disabled={!!merchant}
            />
            {merchant && <p className="text-xs text-slate-500">{t("emailCannotBeChanged")}</p>}
          </div>

          {!merchant && (
            <div className="space-y-2">
              <Label>{t("password")} *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => handleChange("password", e.target.value)}
                placeholder={t("passwordPlaceholder")}
              />
            </div>
          )}

          {/* API Key (Edit mode only) */}
          {merchant && (
            <div className="space-y-2">
              <Label>{t("apiKey")}</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-3 bg-slate-900 rounded-lg font-mono text-sm text-white">
                  {showApiKey ? merchant.api_key : "••••••••••••••••••••••••••••••••"}
                </div>
                <Button variant="outline" size="icon" onClick={() => setShowApiKey(!showApiKey)}>
                  {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button variant="outline" size="icon" onClick={copyApiKey}>
                  {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>{t("callbackUrlLabel")}</Label>
            <Input
              value={formData.callback_url}
              onChange={(e) => handleChange("callback_url", e.target.value)}
              placeholder={t("callbackUrlPlaceholder")}
            />
          </div>

          {/* Allowed Providers */}
          <div className="space-y-3">
            <Label>{t("allowedProviders")}</Label>
            <div className="grid grid-cols-2 gap-2">
              {providers.map(provider => (
                <label
                  key={provider.code}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${selectedProviders.includes(provider.code)
                    ? "bg-sky-50 border-sky-300"
                    : "bg-white border-slate-200 hover:border-slate-300"
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedProviders.includes(provider.code)}
                    onChange={() => toggleProvider(provider.code)}
                    className="rounded border-slate-300"
                  />
                  <span className="text-sm font-medium">{getProviderName(provider, language)}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Fee Configuration */}
          <div className="space-y-3">
            <Label>{t("feeConfiguration")}</Label>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">{t("percentageFeeLabel")}</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.fee_value}
                  onChange={(e) => handleChange("fee_value", parseFloat(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">{t("minDeposit")}</Label>
                <Input
                  type="number"
                  value={formData.min_deposit}
                  onChange={(e) => handleChange("min_deposit", parseInt(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">{t("maxDeposit")}</Label>
                <Input
                  type="number"
                  value={formData.max_deposit}
                  onChange={(e) => handleChange("max_deposit", parseInt(e.target.value))}
                />
              </div>
            </div>
          </div>

          {/* Toggles */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2">
              <Switch
                checked={formData.deposit_enabled}
                onCheckedChange={(v) => handleChange("deposit_enabled", v)}
              />
              <span className="text-sm">{t("deposits")}</span>
            </label>
            <label className="flex items-center gap-2">
              <Switch
                checked={formData.withdrawal_enabled}
                onCheckedChange={(v) => handleChange("withdrawal_enabled", v)}
              />
              <span className="text-sm">{t("withdrawals")}</span>
            </label>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !formData.name || (!merchant && (!formData.email || !formData.password))}
            className="bg-sky-600 hover:bg-sky-700"
          >
            {saving ? t("saving") : merchant ? t("updateMerchant") : t("createMerchantBtn")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}